import {
  BoxGeometry,
  Color,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
} from 'three';
import type { HouseConfig } from '@/model/schema';

/**
 * Debug overlays: room rectangles per story, facade opening anchors, roof
 * bounding box. Behind the `?debug=1` flag or the UI toggle (see uiStore).
 */
export function buildDebugOverlays(cfg: HouseConfig): Group {
  const group = new Group();
  group.name = 'DebugOverlays';

  const roomEdge = new LineBasicMaterial({ color: new Color('#f5a623') });
  const openingEdge = new LineBasicMaterial({ color: new Color('#7acc7a') });
  const roofEdge = new LineBasicMaterial({ color: new Color('#d06666') });

  // Room rectangles per story (drawn as thin wire planes just above each floor).
  for (const story of cfg.stories) {
    for (const room of story.rooms) {
      const rectGeo = new PlaneGeometry(room.width, room.depth);
      const edges = new EdgesGeometry(rectGeo);
      const lines = new LineSegments(edges, roomEdge);
      lines.rotation.x = -Math.PI / 2;
      lines.position.set(
        cfg.house.originX + room.x + room.width / 2,
        story.elevation + cfg.house.floorThickness + 0.02,
        cfg.house.originZ + room.z + room.depth / 2,
      );
      group.add(lines);
      rectGeo.dispose();
    }
  }

  // Window anchors as small cubes in front of each opening center.
  const anchorGeo = new BoxGeometry(0.12, 0.12, 0.12);
  const anchorMat = new MeshBasicMaterial({ color: new Color('#7acc7a') });
  void openingEdge; // line material reserved for future use
  for (const story of cfg.stories) {
    for (const w of story.windows) {
      const mesh = new Mesh(anchorGeo, anchorMat);
      const { x, z } = facadeToWorld(cfg, w.side, w.offset + w.width / 2);
      mesh.position.set(x, story.elevation + w.sillHeight + w.height / 2, z);
      group.add(mesh);
    }
  }

  // Roof bbox.
  const roofBox = new BoxGeometry(
    cfg.house.footprintWidth + 2 * cfg.roof.overhang,
    cfg.roof.height,
    cfg.house.footprintDepth + 2 * cfg.roof.overhang,
  );
  const roofEdges = new EdgesGeometry(roofBox);
  const roofLines = new LineSegments(roofEdges, roofEdge);
  roofLines.position.set(
    cfg.house.originX + cfg.house.footprintWidth / 2,
    cfg.house.floorThickness + cfg.stories.length * cfg.house.storyHeight + cfg.roof.height / 2,
    cfg.house.originZ + cfg.house.footprintDepth / 2,
  );
  group.add(roofLines);
  roofBox.dispose();

  return group;
}

function facadeToWorld(
  cfg: HouseConfig,
  side: 'front' | 'back' | 'left' | 'right',
  u: number,
): { x: number; z: number } {
  const { footprintWidth, footprintDepth, originX, originZ } = cfg.house;
  const cx = originX + footprintWidth / 2;
  const cz = originZ + footprintDepth / 2;
  switch (side) {
    case 'front': return { x: originX + u, z: cz - footprintDepth / 2 };
    case 'back':  return { x: originX + (footprintWidth - u), z: cz + footprintDepth / 2 };
    case 'left':  return { x: cx - footprintWidth / 2, z: originZ + (footprintDepth - u) };
    case 'right': return { x: cx + footprintWidth / 2, z: originZ + u };
  }
}
